import { useEffect, useState } from 'react';
import { useStripe, PaymentElement, useElements } from '@stripe/react-stripe-js';
import BillingDetailsForm from './BillingDetailsForm';
import { createAccessPayment, createAccessPaymentReset, confirmAccessPayment, confirmAccessPaymentReset } from 'redux/Payments/PaymentsActions';
import { getUserData } from 'redux/UserAuthentication/LoginActions';
import { connect, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import {
    Card,
    Row,
    Col,
    CardBody,
    CardHeader
} from "reactstrap";
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { getPlanById, getEffectivePrice, getEffectivePricePerMonth, getSpecialtyFields, getPlansForSpecialty, ADDON_DISCOUNT_PERCENT, getAddonPrice, getBundleById, isTrialValid } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';

const SubscriptionForm = (props) => {
    const history = useHistory()
    const isAddon = props.isAddon || false;
    const isBundle = props.isBundle || false;
    const bundleId = props.bundleId || null;
    const bundleTotalPrice = props.bundleTotalPrice || null;
    const bundleName = props.bundleName || null;
    const bundleSpecialties = props.bundleSpecialties || null;
    const bundle = bundleId ? getBundleById(bundleId) : null;
    const activeSpecialty = props.overrideSpecialty || props.activeSpecialty || 'pmr';
    const specialtyPlans = getPlansForSpecialty(activeSpecialty);
    const specialtyFields = getSpecialtyFields(activeSpecialty);
    const [plan, setPlan] = useState(props.selectedPlan || specialtyPlans[0].priceId)
    const [selectedPlanDetails, setSelectedPlanDetails] = useState(null)
    const [paymentIntentId, setPaymentIntentId] = useState(null)
    const hasActiveTrial = isTrialValid(props.userDetails?.[specialtyFields.trialStartDate]);

    const getDisplayPrice = (planObj) => {
        if (isBundle && bundleTotalPrice != null) return bundleTotalPrice;
        if (isAddon && planObj) return getAddonPrice(planObj, hasActiveTrial);
        return getEffectivePrice(planObj, hasActiveTrial);
    };
    const getDisplayPricePerMonth = (planObj) => {
        if (isBundle && bundleTotalPrice != null && planObj) return bundleTotalPrice / planObj.months;
        if (isAddon && planObj) return getAddonPrice(planObj, hasActiveTrial) / planObj.months;
        return getEffectivePricePerMonth(planObj, hasActiveTrial);
    };

    const options = {
        layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: false,
            spacedAccordionItems: true,
        },
        terms: {
            card: "never"
        },
        fields: {
            billingDetails: {
                name: "never"
            }
        }
    }

    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [hadActiveSubscription, setHadActiveSubscription] = useState(false);

    const handleError = (error) => {
        setLoading(false);
        Swal.fire({
            title: 'Error',
            text: error.message || error,
            icon: 'error',
        });
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setLoading(true);

        // Capture pre-purchase state so congrats page can show the right message
        setHadActiveSubscription(!!(
            props.userDetails?.[specialtyFields.subscriptionEndDate] &&
            new Date(props.userDetails[specialtyFields.subscriptionEndDate]) > new Date()
        ));

        // Validate payment element
        const { error: submitError } = await elements.submit();
        if (submitError) {
            handleError(submitError);
            return;
        }

        // Create payment intent on backend
        dispatch(createAccessPayment({
            planId: plan,
            email: props.userDetails.email,
            specialty: activeSpecialty,
            isAddon,
            isBundle,
            bundleId: bundleId || '',
        }));
    }

    useEffect(() => {
        if (props.selectedPlan) {
            setPlan(props.selectedPlan)
        }
    }, [props.selectedPlan])

    useEffect(() => {
        const planDetails = getPlanById(plan);
        setSelectedPlanDetails(planDetails);
    }, [plan])

    // Handle payment intent creation response
    useEffect(() => {
        const processPayment = async () => {
            if (props.createAccessPayment.success && props.createAccessPayment.data) {
                const { clientSecret, paymentIntentId: piId, finalPrice: backendPrice } = props.createAccessPayment.data;
                setPaymentIntentId(piId);

                // Check if backend price differs from displayed price (trial may have expired mid-session)
                const displayedPrice = selectedPlanDetails ? getDisplayPrice(selectedPlanDetails) : 0;
                if (backendPrice && displayedPrice && backendPrice !== displayedPrice) {
                    const result = await Swal.fire({
                        title: 'Price Updated',
                        text: `The price for this plan is now $${backendPrice}. Would you like to continue?`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: `Pay $${backendPrice}`,
                        cancelButtonText: 'Cancel',
                        confirmButtonColor: '#5e72e4',
                    });
                    if (!result.isConfirmed) {
                        setLoading(false);
                        dispatch(createAccessPaymentReset());
                        return;
                    }
                }

                // Confirm the payment with Stripe
                const { error } = await stripe.confirmPayment({
                    elements,
                    clientSecret,
                    confirmParams: {
                        return_url: window.location.origin + '/client/congrats',
                    },
                    redirect: 'if_required'
                });

                if (error) {
                    handleError(error);
                    dispatch(createAccessPaymentReset());
                } else {
                    // Payment succeeded, confirm on backend
                    dispatch(confirmAccessPayment({
                        paymentIntentId: piId,
                        planId: plan,
                        email: props.userDetails.email,
                        specialty: activeSpecialty,
                        isAddon,
                        isBundle,
                        bundleId: bundleId || '',
                    }));
                }
            }

            if (props.createAccessPayment.error) {
                handleError(props.createAccessPayment.error);
                dispatch(createAccessPaymentReset());
            }
        };

        processPayment();
    }, [props.createAccessPayment]);

    // Handle payment confirmation response
    useEffect(() => {
        if (props.confirmAccessPayment.success) {
            setLoading(false);
            const newEndDate = props.confirmAccessPayment.data?.subscriptionEndDate;
            dispatch(createAccessPaymentReset());
            dispatch(confirmAccessPaymentReset());

            // Refresh user data
            dispatch(getUserData({ onLogin: true }));

            Swal.fire({
                title: hadActiveSubscription ? 'Subscription Extended!' : 'Success!',
                text: hadActiveSubscription
                    ? 'Your subscription has been extended.'
                    : 'Your subscription is now active!',
                icon: 'success',
                timer: 3000
            }).then(() => {
                history.push({
                    pathname: '/client/congrats',
                    state: {
                        plan: isBundle ? (bundleName || 'Bundle') : (selectedPlanDetails?.name || plan),
                        isExtension: hadActiveSubscription,
                        subscriptionEndDate: newEndDate,
                        isAddon,
                        isBundle,
                        bundleSpecialties: isBundle ? bundleSpecialties : null,
                        specialty: activeSpecialty,
                    }
                });
            });
        }

        if (props.confirmAccessPayment.error) {
            setLoading(false);
            handleError(props.confirmAccessPayment.error);
            dispatch(confirmAccessPaymentReset());
        }
    }, [props.confirmAccessPayment]);

    const handleSelectPlan = (e, planId) => {
        e.preventDefault();
        setPlan(planId)
    }

    const getColorClass = (color) => {
        const colorMap = {
            info: 'bg-info',
            success: 'bg-success',
            warning: 'bg-warning',
            primary: 'bg-primary',
            default: 'bg-default',
            danger: 'bg-danger'
        };
        return colorMap[color] || 'bg-info';
    };

    const getBtnColorClass = (color, isSelected) => {
        if (isSelected) {
            const colorMap = {
                info: 'btn-info',
                success: 'btn-success',
                warning: 'btn-warning',
                primary: 'btn-primary',
                default: 'btn-default',
                danger: 'btn-danger'
            };
            return colorMap[color] || 'btn-info';
        }
        return 'btn-secondary';
    };

    return (
        <Row>
            <Col xl="6" lg="12" md="12" className='mb-3'>
                <Card className='border-0 shadow w-100'>
                    <CardHeader className={selectedPlanDetails ? getColorClass(selectedPlanDetails.color) : 'bg-info'}>
                        {isBundle && bundle && (
                            <div className="text-center mb-2">
                                <span className="badge badge-warning" style={{ fontSize: '1rem', padding: '8px 16px' }}>
                                    <i className="fas fa-layer-group mr-1"></i> Bundle — Save {bundle.discountPercent}%
                                </span>
                            </div>
                        )}
                        {isAddon && !isBundle && (
                            <div className="text-center mb-2">
                                <span className="badge badge-danger" style={{ fontSize: '1rem', padding: '8px 16px' }}>
                                    <i className="fas fa-tag mr-1"></i> Add-On — Save {ADDON_DISCOUNT_PERCENT}%
                                </span>
                            </div>
                        )}
                        <h1 className="h2 mb-0 text-center text-white">
                            {isBundle ? (
                                <><i className="fas fa-layer-group mr-2" style={{ fontSize: '1.2rem' }}></i>{bundleName || 'Bundle'}</>
                            ) : (
                                <><i className={`${getSpecialty(activeSpecialty).icon} mr-2`} style={{ fontSize: '1.2rem' }}></i>{getSpecialty(activeSpecialty).name} Prep</>
                            )}
                        </h1>
                        <h2 className="h3 mb-0 text-center text-white-50 mt-1">
                            {selectedPlanDetails?.name || 'Select a Plan'}
                        </h2>
                        <h2 className="display-2 mb-0 text-center text-white">
                            ${selectedPlanDetails ? getDisplayPrice(selectedPlanDetails) : '0'}
                            {selectedPlanDetails && isAddon && (
                                <span className="h4 text-white-50 ml-2" style={{ textDecoration: 'line-through' }}>
                                    ${getEffectivePrice(selectedPlanDetails)}
                                </span>
                            )}
                        </h2>
                        <p className='display-4 font-italic text-center text-white'>
                            {selectedPlanDetails ? `${selectedPlanDetails.months} months access` : ''}
                        </p>
                        {selectedPlanDetails && (
                            <p className='h3 text-white-50 text-center'>
                                ${getDisplayPricePerMonth(selectedPlanDetails).toFixed(2)}/month
                            </p>
                        )}
                    </CardHeader>
                    <CardBody>
                        <Row className="pl-2 pr-2 h-100">
                            <Col lg='12 text-center' className='d-flex align-items-center flex-column h-100'>
                                <h3 className="h3 mb-0 text-dark mb-3">
                                    {isBundle
                                        ? 'Get full access to multiple specialty question banks at a bundled discount.'
                                        : (selectedPlanDetails?.description || `Choose a subscription plan to get full access to ${getSpecialty(activeSpecialty).name} Prep features.`)}
                                </h3>
                                {isBundle && bundleSpecialties && (
                                    <div className="mb-3">
                                        <h4 className="text-dark mb-2">Included Specialties:</h4>
                                        <ul className="list-unstyled">
                                            {bundleSpecialties.map(specId => (
                                                <li key={specId} className="py-1">
                                                    <i className={`${getSpecialty(specId).icon} text-${getSpecialty(specId).color} mr-2`}></i>
                                                    {getSpecialty(specId).name} Prep
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <ul className="list-unstyled mt-3 text-left">
                                    <li className="py-1"><i className="fas fa-check text-success mr-2"></i>Full Question Bank Access</li>
                                    <li className="py-1"><i className="fas fa-check text-success mr-2"></i>Performance Analytics</li>
                                    <li className="py-1"><i className="fas fa-check text-success mr-2"></i>Unlimited Practice Tests</li>
                                    <li className="py-1"><i className="fas fa-check text-success mr-2"></i>Progress Tracking</li>
                                </ul>
                                <div className="mt-3 text-muted small">
                                    <i className="fas fa-info-circle mr-1"></i>
                                    One-time payment. Renew anytime after expiration.
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Col>
            <Col xl="6" lg="12" md="12">
                <Card className='border-0 shadow'>
                    <CardBody>
                        <form onSubmit={handleSubmit}>
                            <p className='h1 text-dark text-center'>Payment Form</p>
                            <p className='h2 text-dark'>Select Plan</p>

                            <Row>
                                {specialtyPlans.map((subscriptionPlan) => {
                                    const effectivePrice = getDisplayPrice(subscriptionPlan);
                                    const strikePrice = isAddon ? getEffectivePrice(subscriptionPlan) : null;
                                    return (
                                    <Col md="6" className="mb-2" key={subscriptionPlan.id}>
                                        <button
                                            className={`btn ${getBtnColorClass(subscriptionPlan.color, plan === subscriptionPlan.priceId)} w-100`}
                                            onClick={e => handleSelectPlan(e, subscriptionPlan.priceId)}
                                            style={{ fontSize: '0.85rem', padding: '10px 8px' }}
                                        >
                                            {subscriptionPlan.months} mo - ${effectivePrice}
                                            {strikePrice && <span className="ml-1" style={{ textDecoration: 'line-through', opacity: 0.7 }}>${strikePrice}</span>}
                                            {subscriptionPlan.popular && !isAddon && <span className="badge badge-danger ml-1">Best</span>}
                                        </button>
                                    </Col>
                                )})}
                            </Row>
                            <br />

                            <p className='h2 text-dark'>Billing</p>
                            <BillingDetailsForm />
                            <br />
                            <p className='h2 text-dark'>Payment</p>
                            <PaymentElement options={options} />
                            <br />
                            <button className='btn btn-primary btn-lg w-100' disabled={!stripe || loading}>
                                {loading ? 'Processing...' : `Pay $${selectedPlanDetails ? getDisplayPrice(selectedPlanDetails) : '0'}`}
                            </button>
                        </form>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    )
}

const mapStateToProps = state => {
    return {
        createAccessPayment: state.createAccessPayment,
        confirmAccessPayment: state.confirmAccessPayment,
        userDetails: state.loginAuth.userDetails,
        activeSpecialty: state.specialty.activeSpecialty,
    }
}

export default connect(mapStateToProps)(SubscriptionForm)
